import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Plus, Trash2, Edit, Save, X, Users, Palette, Globe, 
  Search, Image as ImageIcon, Building, Wand2
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API } from "./utils";
import { extractColors } from "../../utils/colorExtractor";
import { ImageUpload } from "./ImageUpload";

export const TeamsTab = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ total: 0, by_conference: [] });
  const [extractingColor, setExtractingColor] = useState(false);
  const [colorExtractError, setColorExtractError] = useState("");

  const [form, setForm] = useState({
    name: "",
    abbreviation: "",
    mascot: "",
    logo_url: "",
    primary_color: "#FF5722",
    secondary_color: "#000000",
    city: "",
    state: "",
    conference: "",
    division: "",
    website_url: ""
  });

  // Auto-extract color from logo
  const handleExtractColor = async () => {
    if (!form.logo_url) return;
    
    setExtractingColor(true);
    setColorExtractError("");
    
    try {
      const colors = await extractColors(form.logo_url);
      setForm(prev => ({
        ...prev,
        primary_color: colors.primary,
        secondary_color: colors.secondary
      }));
    } catch (error) {
      setColorExtractError(error.message || "Failed to extract colors. Try manual selection.");
    } finally {
      setExtractingColor(false);
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchStats();
  }, []);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`);
      setTeams(res.data);
    } catch (err) {
      console.error("Error fetching teams:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/teams/stats/summary`);
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editing) {
        await axios.put(`${API}/teams/${editing}`, form);
      } else {
        await axios.post(`${API}/teams`, form);
      }
      fetchTeams();
      fetchStats();
      closeForm();
    } catch (err) {
      console.error("Error saving team:", err);
      alert(err.response?.data?.detail || "Failed to save team");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (team) => {
    setEditing(team.id);
    setForm({
      name: team.name || "",
      abbreviation: team.abbreviation || "",
      mascot: team.mascot || "",
      logo_url: team.logo_url || "",
      primary_color: team.primary_color || "#FF5722",
      secondary_color: team.secondary_color || "#000000",
      city: team.city || "",
      state: team.state || "",
      conference: team.conference || "",
      division: team.division || "",
      website_url: team.website_url || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm("Delete this team? This action cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      fetchTeams();
      fetchStats();
    } catch (err) {
      console.error("Error deleting team:", err);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({
      name: "",
      abbreviation: "",
      mascot: "",
      logo_url: "",
      primary_color: "#FF5722",
      secondary_color: "#000000",
      city: "",
      state: "",
      conference: "",
      division: "",
      website_url: ""
    });
  };

  // Filter teams by search query
  const filteredTeams = teams.filter(team => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      team.name?.toLowerCase().includes(q) ||
      team.abbreviation?.toLowerCase().includes(q) ||
      team.mascot?.toLowerCase().includes(q) ||
      team.city?.toLowerCase().includes(q) ||
      team.conference?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">
            Team Manager
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Manage team logos, colors, and info for use across the site
          </p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-white text-black hover:bg-zinc-200"
          data-testid="add-team-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Team
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-zinc-900/50 border border-white/10 p-4 text-center">
          <div className="text-3xl font-bold text-white">{stats.total}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Total Teams</div>
        </div>
        {stats.by_conference.slice(0, 3).map((c, i) => (
          <div key={i} className="bg-zinc-900/50 border border-white/10 p-4 text-center">
            <div className="text-3xl font-bold text-orange-500">{c.count}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{c.conference}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-900 border border-white/10 text-white pl-10 pr-4 py-2 text-sm"
            data-testid="team-search-input"
          />
        </div>
      </div>

      {/* Team Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-white/10 w-full max-w-2xl my-8"
          >
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold uppercase text-white">
                {editing ? "Edit Team" : "Add New Team"}
              </h3>
              <button onClick={closeForm} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Team Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Texas Longhorns"
                    required
                    className="w-full bg-black border border-white/10 text-white px-4 py-3"
                    data-testid="team-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Abbreviation
                  </label>
                  <input
                    type="text"
                    value={form.abbreviation}
                    onChange={(e) => setForm({ ...form, abbreviation: e.target.value.toUpperCase() })}
                    placeholder="TEX"
                    maxLength={5}
                    className="w-full bg-black border border-white/10 text-white px-4 py-3"
                  />
                </div>
              </div>

              <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Mascot
                  </label>
                  <input
                    type="text"
                    value={form.mascot}
                    onChange={(e) => setForm({ ...form, mascot: e.target.value })}
                    placeholder="Longhorns"
                    className="w-full bg-black border border-white/10 text-white px-4 py-3"
                  />
                </div>

              {/* Logo */}
              <ImageUpload
                value={form.logo_url}
                onChange={(url) => setForm({ ...form, logo_url: url })}
                label="Team Logo"
                placeholder="https://example.com/logo.png"
                previewSize="lg"
              />
              
              {/* Auto Extract Color Button */}
              {form.logo_url && (
                <div className="p-3 bg-zinc-800/50 border border-white/10 rounded space-y-2">
                  <button
                    type="button"
                    onClick={handleExtractColor}
                    disabled={extractingColor}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white text-sm font-bold rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="extract-color-btn"
                  >
                    <Wand2 className={`w-4 h-4 ${extractingColor ? 'animate-spin' : ''}`} />
                    {extractingColor ? 'Extracting Colors...' : '✨ Auto-Extract Colors from Logo'}
                  </button>
                  
                  {colorExtractError && (
                    <p className="text-xs text-red-400">{colorExtractError}</p>
                  )}
                </div>
              )}

              {/* Colors */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Primary Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={form.primary_color}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      className="w-12 h-10 bg-black border border-white/10 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={form.primary_color}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      className="flex-1 bg-black border border-white/10 text-white px-4 py-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={form.secondary_color}
                      onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                      className="w-12 h-10 bg-black border border-white/10 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={form.secondary_color}
                      onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                      className="flex-1 bg-black border border-white/10 text-white px-4 py-2"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Austin"
                    className="w-full bg-black border border-white/10 text-white px-4 py-3"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="TX"
                    maxLength={2}
                    className="w-full bg-black border border-white/10 text-white px-4 py-3"
                  />
                </div>
              </div>

              {/* Organization */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Conference
                  </label>
                  <input
                    type="text"
                    value={form.conference}
                    onChange={(e) => setForm({ ...form, conference: e.target.value })}
                    placeholder="SEC"
                    className="w-full bg-black border border-white/10 text-white px-4 py-3"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Division
                  </label>
                  <input
                    type="text"
                    value={form.division}
                    onChange={(e) => setForm({ ...form, division: e.target.value })}
                    placeholder="East"
                    className="w-full bg-black border border-white/10 text-white px-4 py-3"
                  />
                </div>
              </div>

              {/* Website */}
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={form.website_url}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  placeholder="https://texassports.com"
                  className="w-full bg-black border border-white/10 text-white px-4 py-3"
                />
              </div>

              {/* Preview */}
              <div className="p-4 bg-zinc-800/50 border border-white/10">
                <p className="text-xs font-bold uppercase text-zinc-400 mb-3">Preview</p>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-16 h-16 rounded-lg flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${form.primary_color}40, ${form.secondary_color}40)`,
                      border: `2px solid ${form.primary_color}`
                    }}
                  >
                    {form.logo_url ? (
                      <img src={form.logo_url} alt="" className="w-12 h-12 object-contain" />
                    ) : (
                      <Users className="w-8 h-8" style={{ color: form.primary_color }} />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{form.name || "Team Name"}</p>
                    <p className="text-zinc-500 text-sm">
                      {[form.city, form.state].filter(Boolean).join(", ") || "Location"} 
                      {form.mascot && ` • ${form.mascot}`}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {form.conference && (
                        <span className="text-xs px-2 py-0.5 bg-white/10">{form.conference}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !form.name}
                  className="flex-1 bg-white text-black hover:bg-zinc-200"
                  data-testid="save-team-btn"
                >
                  {saving ? "Saving..." : editing ? "Update Team" : "Add Team"}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Teams Grid */}
      {filteredTeams.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/10 p-12 text-center">
          <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-white font-bold mb-2">
            {searchQuery ? "No Teams Found" : "No Teams Added"}
          </h3>
          <p className="text-zinc-500 mb-6">
            {searchQuery 
              ? "Try adjusting your search" 
              : "Add teams to reuse their logos and colors across the site."
            }
          </p>
          {!searchQuery && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-zinc-800 text-white hover:bg-zinc-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Team
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeams.map((team) => (
            <div
              key={team.id}
              className="bg-zinc-900/50 border border-white/10 overflow-hidden hover:bg-zinc-900/70 transition-colors"
            >
              {/* Color Bar */}
              <div 
                className="h-2"
                style={{ 
                  background: `linear-gradient(90deg, ${team.primary_color}, ${team.secondary_color || team.primary_color})` 
                }}
              />

              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Logo */}
                  <div 
                    className="w-14 h-14 rounded flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: team.primary_color + '20' }}
                  >
                    {team.logo_url ? (
                      <img 
                        src={team.logo_url} 
                        alt={team.name} 
                        className="w-10 h-10 object-contain"
                      />
                    ) : (
                      <span 
                        className="text-lg font-bold"
                        style={{ color: team.primary_color }}
                      >
                        {team.abbreviation || team.name.slice(0, 2)}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold truncate">{team.name}</h3>
                    <p className="text-zinc-500 text-sm">
                      {team.mascot && `${team.mascot} • `}
                      {[team.city, team.state].filter(Boolean).join(", ")}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(team)}
                      className="p-1.5 text-zinc-500 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(team.id)}
                      className="p-1.5 text-zinc-500 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                  {team.conference && (
                    <span className="text-[10px] px-2 py-0.5 bg-white/10 text-zinc-400">
                      {team.conference}
                    </span>
                  )}
                  {team.website_url && (
                    <a 
                      href={team.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-zinc-500 hover:text-white"
                    >
                      <Globe className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>

                {/* Colors */}
                <div className="flex items-center gap-2 mt-2">
                  <div 
                    className="w-5 h-5 rounded border border-white/10"
                    style={{ backgroundColor: team.primary_color }}
                    title={`Primary: ${team.primary_color}`}
                  />
                  <div 
                    className="w-5 h-5 rounded border border-white/10"
                    style={{ backgroundColor: team.secondary_color }}
                    title={`Secondary: ${team.secondary_color}`}
                  />
                  <span className="text-[10px] text-zinc-600 ml-1">
                    {team.primary_color}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default TeamsTab;
